/**
 * Die Felder von „Liefervertrag anlegen" und „Liefervertrag bearbeiten"
 * (Spec 46c, „Blatt") — ein Formular, zwei Modi. Testid-Präfix `supply-create-*`
 * bzw. `supply-edit-*`.
 *
 * Zwei Gruppen: die Hauptfelder (Anbieter, Beginn, Laufzeit, Verlängerung,
 * Kündigungsfrist) und „Weitere Angaben" (Preise, Preisgarantie, Nummern,
 * „Gekündigt zum"). Der Anbieter ist ein Platz (`providerField`): Absender
 * gehören `features/documents`, die Auswahl bringt der Container mit.
 * Die Einheiten der Kündigungsfrist kommen aus `GET /energy/meter-options`.
 */

import type { ReactNode } from 'react'

import { EnumSelectField } from '@/components/EnumSelectField'
import { FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import type { MeterOptions } from '@/features/energy/api'
import { digitsOnly } from '@/features/energy/meter-form'
import { decimalOnly } from '@/features/energy/supply-form'
import type { SupplyFormValues } from '@/features/energy/supply-form'

export type SupplyPrefix = 'supply-create' | 'supply-edit'

interface FieldsProps {
  readonly prefix: SupplyPrefix
  readonly form: SupplyFormValues
  readonly onChange: (name: keyof SupplyFormValues) => (value: string) => void
  readonly error: FieldError | undefined
}

interface PrimaryProps extends FieldsProps {
  /** Das Anbieterfeld; bekommt den Fehler an `provider_id`. */
  readonly providerField: (error: FieldError | undefined) => ReactNode
  readonly noticeUnits: MeterOptions['notice_units']
  readonly isOptionsPending: boolean
}

function Suffix({ children }: { readonly children: string }) {
  return <span className="text-label text-muted flex min-h-(--tap-min) items-center">{children}</span>
}

export function SupplyPrimaryFields({ prefix, form, onChange, error, providerField, noticeUnits, isOptionsPending }: PrimaryProps) {
  return (
    <>
      {providerField(error)}

      <FormField
        label="Beginn"
        testId={`${prefix}-starts-on`}
        apiField="starts_on"
        errorPrefix={prefix}
        type="date"
        required
        value={form.startsOn}
        onChange={onChange('startsOn')}
        error={error}
      />
      <FormField
        label="Laufzeit bis"
        testId={`${prefix}-ends-on`}
        apiField="ends_on"
        errorPrefix={prefix}
        type="date"
        value={form.endsOn}
        onChange={onChange('endsOn')}
        error={error}
      />
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <FormField
            label="Verlängerung"
            testId={`${prefix}-renewal`}
            apiField="renewal_months"
            errorPrefix={prefix}
            inputMode="numeric"
            value={form.renewal}
            onChange={(value) => {
              onChange('renewal')(digitsOnly(value))
            }}
            error={error}
          />
        </div>
        <Suffix>Monate</Suffix>
      </div>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <FormField
            label="Kündigungsfrist"
            testId={`${prefix}-notice-value`}
            apiField="notice_value"
            errorPrefix={prefix}
            inputMode="numeric"
            value={form.noticeValue}
            onChange={(value) => {
              onChange('noticeValue')(digitsOnly(value))
            }}
            error={error}
          />
        </div>
        <div className="w-32 shrink-0">
          <EnumSelectField
            label="Einheit"
            testId={`${prefix}-notice-unit`}
            apiField="notice_unit"
            errorPrefix={prefix}
            options={noticeUnits}
            isPending={isOptionsPending}
            value={form.noticeUnit}
            onChange={onChange('noticeUnit')}
            error={error}
          />
        </div>
      </div>
    </>
  )
}

function MoneyField({
  prefix,
  name,
  apiField,
  label,
  suffix,
  value,
  onChange,
  error,
}: {
  readonly prefix: SupplyPrefix
  readonly name: string
  readonly apiField: string
  readonly label: string
  readonly suffix: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
}) {
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <FormField
          label={label}
          testId={`${prefix}-${name}`}
          apiField={apiField}
          errorPrefix={prefix}
          inputMode="decimal"
          value={value}
          onChange={(next) => {
            onChange(decimalOnly(next))
          }}
          error={error}
        />
      </div>
      <Suffix>{suffix}</Suffix>
    </div>
  )
}

export function SupplyMoreFields({ prefix, form, onChange, error }: FieldsProps) {
  return (
    <>
      <MoneyField
        prefix={prefix}
        name="unit-price"
        apiField="unit_price_centicents"
        label="Arbeitspreis"
        suffix="ct"
        value={form.unitPrice}
        onChange={onChange('unitPrice')}
        error={error}
      />
      <MoneyField
        prefix={prefix}
        name="base-price"
        apiField="base_price_cents"
        label="Grundpreis"
        suffix="€/Monat"
        value={form.basePrice}
        onChange={onChange('basePrice')}
        error={error}
      />
      <MoneyField
        prefix={prefix}
        name="installment"
        apiField="installment_cents"
        label="Abschlag"
        suffix="€/Monat"
        value={form.installment}
        onChange={onChange('installment')}
        error={error}
      />
      <FormField
        label="Preisgarantie bis"
        testId={`${prefix}-price-guarantee`}
        apiField="price_guarantee_until"
        errorPrefix={prefix}
        type="date"
        value={form.priceGuarantee}
        onChange={onChange('priceGuarantee')}
        error={error}
      />
      <FormField
        label="Kundennummer"
        testId={`${prefix}-customer-number`}
        apiField="customer_number"
        errorPrefix={prefix}
        value={form.customerNumber}
        onChange={onChange('customerNumber')}
        error={error}
      />
      <FormField
        label="Vertragsnummer"
        testId={`${prefix}-contract-number`}
        apiField="contract_number"
        errorPrefix={prefix}
        value={form.contractNumber}
        onChange={onChange('contractNumber')}
        error={error}
      />
      <FormField
        label="Gekündigt zum"
        testId={`${prefix}-terminated-on`}
        apiField="terminated_on"
        errorPrefix={prefix}
        type="date"
        value={form.terminatedOn}
        onChange={onChange('terminatedOn')}
        error={error}
      />
    </>
  )
}
